import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Check, X, DollarSign, ExternalLink, Users, CreditCard, 
  Edit, Star, Image, Video, Upload, Pause, Play, 
  MoreVertical, Eye, RefreshCw, Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function AdminListeners() {
  const [listeners, setListeners] = useState([]);
  const [referrerOptions, setReferrerOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pricing dialog
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [selectedListener, setSelectedListener] = useState(null);
  const [pricing, setPricing] = useState({ 
    price: 50, 
    listenerPercentage: 80,
    referrerId: "",
    referrerPercentage: 0,
    referrerSessionsLimit: ""
  });
  
  // Edit profile dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    bio: "",
    experience: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Manual stats dialog
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [statsData, setStatsData] = useState({
    manual_rating: "",
    manual_sessions_count: "",
    manual_review_note: ""
  });
  
  // Upload media dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState("photo"); // "photo" or "video"
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchListeners();
    fetchReferrerOptions();
  }, []);

  const fetchListeners = async () => {
    try {
      const response = await axios.get(`${API}/admin/listeners`);
      setListeners(response.data);
    } catch (error) {
      console.error("Error fetching listeners:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrerOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/listeners-for-referral`);
      setReferrerOptions(response.data);
    } catch (error) {
      console.error("Error fetching referrer options:", error);
    }
  };

  const handleApproval = async (listenerId, action) => {
    try {
      await axios.post(`${API}/admin/approve-profile`, {
        listener_id: listenerId,
        action
      });
      await fetchListeners();
      toast.success(`Perfil ${action === 'approve' ? 'aprobado' : 'rechazado'}`);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error al actualizar el perfil");
    }
  };

  const handleStatusChange = async (listenerId, newStatus) => {
    try {
      await axios.post(`${API}/admin/listener/update-status`, {
        listener_id: listenerId,
        status: newStatus
      });
      await fetchListeners();
      toast.success(`Estado actualizado a ${newStatus === 'approved' ? 'Aprobado' : newStatus === 'suspended' ? 'Suspendido' : 'Rechazado'}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handlePricing = async () => {
    if (!selectedListener) return;
    
    const totalPercentage = pricing.listenerPercentage + (pricing.referrerId ? pricing.referrerPercentage : 0);
    if (totalPercentage > 100) {
      toast.error("Los porcentajes no pueden sumar más de 100%");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/set-pricing`, {
        listener_id: selectedListener.id,
        price_per_session: pricing.price,
        listener_percentage: pricing.listenerPercentage,
        referrer_id: pricing.referrerId || null,
        referrer_percentage: pricing.referrerId ? pricing.referrerPercentage : 0,
        referrer_sessions_limit: pricing.referrerSessionsLimit ? parseInt(pricing.referrerSessionsLimit) : null
      });
      await fetchListeners();
      setShowPricingDialog(false);
      toast.success("Configuración actualizada");
    } catch (error) {
      console.error("Error updating pricing:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar la configuración");
    }
  };

  const handleEditProfile = async () => {
    if (!selectedListener) return;
    setSaving(true);
    
    try {
      await axios.post(`${API}/admin/listener/edit-profile`, {
        listener_id: selectedListener.id,
        name: editData.name || null,
        bio: editData.bio || null,
        experience: editData.experience || null
      });
      await fetchListeners();
      setShowEditDialog(false);
      toast.success("Perfil actualizado");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleSetManualStats = async () => {
    if (!selectedListener) return;
    setSaving(true);
    
    try {
      await axios.post(`${API}/admin/listener/set-manual-stats`, {
        listener_id: selectedListener.id,
        manual_rating: statsData.manual_rating ? parseFloat(statsData.manual_rating) : null,
        manual_sessions_count: statsData.manual_sessions_count ? parseInt(statsData.manual_sessions_count) : null,
        manual_review_note: statsData.manual_review_note || null
      });
      await fetchListeners();
      setShowStatsDialog(false);
      toast.success("Estadísticas actualizadas");
    } catch (error) {
      console.error("Error setting stats:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar estadísticas");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedListener) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("listener_id", selectedListener.id);
    formData.append("media_type", uploadType);
    
    try {
      await axios.post(`${API}/admin/listener/upload-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await fetchListeners();
      setShowUploadDialog(false);
      toast.success(`${uploadType === 'photo' ? 'Foto' : 'Video'} subido correctamente`);
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openPricingDialog = (listener) => {
    setSelectedListener(listener);
    setPricing({
      price: listener.price_per_session || 50,
      listenerPercentage: listener.listener_percentage || listener.revenue_percentage || 80,
      referrerId: listener.referrer_id || "",
      referrerPercentage: listener.referrer_percentage || 0,
      referrerSessionsLimit: listener.referrer_sessions_limit ? String(listener.referrer_sessions_limit) : ""
    });
    setShowPricingDialog(true);
  };

  const openEditDialog = (listener) => {
    setSelectedListener(listener);
    setEditData({
      name: listener.name || "",
      bio: listener.bio || "",
      experience: listener.experience || ""
    });
    setShowEditDialog(true);
  };

  const openStatsDialog = (listener) => {
    setSelectedListener(listener);
    setStatsData({
      manual_rating: listener.manual_rating ? String(listener.manual_rating) : "",
      manual_sessions_count: listener.manual_sessions_count ? String(listener.manual_sessions_count) : "",
      manual_review_note: listener.manual_review_note || ""
    });
    setShowStatsDialog(true);
  };

  const openUploadDialog = (listener, type) => {
    setSelectedListener(listener);
    setUploadType(type);
    setShowUploadDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Aprobado</span>;
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pendiente</span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rechazado</span>;
      case 'suspended':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-700">Suspendido</span>;
      default:
        return null;
    }
  };

  const platformPercentage = 100 - pricing.listenerPercentage - (pricing.referrerId ? pricing.referrerPercentage : 0);

  return (
    <AdminLayout title="Listeners">
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : listeners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-600">No hay listeners registrados</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Listener</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Stats</TableHead>
                <TableHead>Stripe</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listeners.map((listener) => (
                <TableRow key={listener.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {listener.photo_url ? (
                        <img 
                          src={getFullUrl(listener.photo_url)} 
                          alt={listener.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                          <Users className="w-5 h-5 text-stone-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{listener.name}</p>
                        <p className="text-xs text-stone-500">@{listener.username}</p>
                        <p className="text-xs text-stone-400">{listener.user_email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(listener.status)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span>{listener.average_rating?.toFixed(1) || "-"}</span>
                      </div>
                      <p className="text-xs text-stone-500">
                        {listener.total_sessions || 0} sesiones
                      </p>
                      {listener.manual_sessions_count && (
                        <p className="text-xs text-blue-500">(manual)</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {listener.stripe_account_id ? (
                      <span className="text-green-600 text-sm flex items-center gap-1">
                        <CreditCard className="w-4 h-4" />
                        Conectado
                      </span>
                    ) : (
                      <span className="text-stone-400 text-sm">Sin conectar</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">${listener.price_per_session || 50}</span>
                    <span className="text-stone-400 text-xs ml-1">USD</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* View Profile */}
                        <DropdownMenuItem onSelect={() => window.open(`/l/${listener.username}`, '_blank')}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver perfil
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Edit Actions */}
                        <DropdownMenuItem onSelect={() => setTimeout(() => openEditDialog(listener), 0)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Editar perfil
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setTimeout(() => openPricingDialog(listener), 0)}>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Configurar precio
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setTimeout(() => openStatsDialog(listener), 0)}>
                          <Star className="w-4 h-4 mr-2" />
                          Stats manuales
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Media Upload */}
                        <DropdownMenuItem onSelect={() => setTimeout(() => openUploadDialog(listener, "photo"), 0)}>
                          <Image className="w-4 h-4 mr-2" />
                          Subir foto
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setTimeout(() => openUploadDialog(listener, "video"), 0)}>
                          <Video className="w-4 h-4 mr-2" />
                          Subir video
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Status Actions */}
                        {listener.status === 'pending' && (
                          <>
                            <DropdownMenuItem onSelect={() => handleApproval(listener.id, 'approve')} className="text-green-600">
                              <Check className="w-4 h-4 mr-2" />
                              Aprobar
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleApproval(listener.id, 'reject')} className="text-red-600">
                              <X className="w-4 h-4 mr-2" />
                              Rechazar
                            </DropdownMenuItem>
                          </>
                        )}
                        {listener.status === 'approved' && (
                          <DropdownMenuItem onSelect={() => handleStatusChange(listener.id, 'suspended')} className="text-amber-600">
                            <Pause className="w-4 h-4 mr-2" />
                            Suspender
                          </DropdownMenuItem>
                        )}
                        {(listener.status === 'suspended' || listener.status === 'rejected') && (
                          <DropdownMenuItem onSelect={() => handleStatusChange(listener.id, 'approved')} className="text-green-600">
                            <Play className="w-4 h-4 mr-2" />
                            Reactivar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar precio</DialogTitle>
            <DialogDescription>
              {selectedListener?.name} (@{selectedListener?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Precio por sesión (USD)</Label>
              <Input
                type="number"
                value={pricing.price}
                onChange={(e) => setPricing({...pricing, price: parseFloat(e.target.value) || 0})}
                min="1"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Porcentaje para listener (%)</Label>
              <Input
                type="number"
                value={pricing.listenerPercentage}
                onChange={(e) => setPricing({...pricing, listenerPercentage: parseInt(e.target.value) || 0})}
                min="0"
                max="100"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Referidor (opcional)</Label>
              <Select 
                value={pricing.referrerId || "none"} 
                onValueChange={(v) => setPricing({...pricing, referrerId: v === "none" ? "" : v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sin referidor" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="none">Sin referidor</SelectItem>
                  {referrerOptions
                    .filter(r => r.id !== selectedListener?.user_id)
                    .map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {pricing.referrerId && (
              <>
                <div>
                  <Label>Porcentaje para referidor (%)</Label>
                  <Input
                    type="number"
                    value={pricing.referrerPercentage}
                    onChange={(e) => setPricing({...pricing, referrerPercentage: parseInt(e.target.value) || 0})}
                    min="0"
                    max="20"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Límite de sesiones (vacío = ilimitado)</Label>
                  <Input
                    type="number"
                    value={pricing.referrerSessionsLimit}
                    onChange={(e) => setPricing({...pricing, referrerSessionsLimit: e.target.value})}
                    min="1"
                    placeholder="Ilimitado"
                    className="mt-1"
                  />
                </div>
              </>
            )}
            <div className="bg-stone-50 rounded-lg p-3">
              <p className="text-sm font-medium">Distribución:</p>
              <div className="text-sm text-stone-600 mt-1">
                <p>Listener: {pricing.listenerPercentage}%</p>
                {pricing.referrerId && <p>Referidor: {pricing.referrerPercentage}%</p>}
                <p>Plataforma: {platformPercentage}%</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPricingDialog(false)}>Cancelar</Button>
              <Button onClick={handlePricing}>Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar perfil</DialogTitle>
            <DialogDescription>
              {selectedListener?.name} (@{selectedListener?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea
                value={editData.bio}
                onChange={(e) => setEditData({...editData, bio: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Experiencia</Label>
              <Textarea
                value={editData.experience}
                onChange={(e) => setEditData({...editData, experience: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
              <Button onClick={handleEditProfile} disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : "Guardar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Stats Dialog */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Estadísticas manuales</DialogTitle>
            <DialogDescription>
              Para listeners con experiencia externa. Estos valores se mostrarán públicamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Calificación (1-5 estrellas)</Label>
              <Input
                type="number"
                value={statsData.manual_rating}
                onChange={(e) => setStatsData({...statsData, manual_rating: e.target.value})}
                min="1"
                max="5"
                step="0.1"
                placeholder="Ej: 4.8"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Número de sesiones</Label>
              <Input
                type="number"
                value={statsData.manual_sessions_count}
                onChange={(e) => setStatsData({...statsData, manual_sessions_count: e.target.value})}
                min="0"
                placeholder="Ej: 150"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Nota interna (no pública)</Label>
              <Textarea
                value={statsData.manual_review_note}
                onChange={(e) => setStatsData({...statsData, manual_review_note: e.target.value})}
                rows={2}
                placeholder="Ej: Viene de BetterHelp con 2 años de experiencia"
                className="mt-1"
              />
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
              <strong>Nota:</strong> Estas estadísticas se mostrarán en el perfil público del listener como si fueran de sesiones reales en la plataforma.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Cancelar</Button>
              <Button onClick={handleSetManualStats} disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : "Guardar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Media Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subir {uploadType === 'photo' ? 'foto' : 'video'}</DialogTitle>
            <DialogDescription>
              Para {selectedListener?.name} (@{selectedListener?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="border-2 border-dashed border-stone-200 rounded-lg p-6 text-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept={uploadType === 'photo' ? 'image/*' : 'video/*'}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-stone-600">Subiendo...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-stone-400" />
                    <p className="text-sm text-stone-600">
                      Haz clic para seleccionar {uploadType === 'photo' ? 'una imagen' : 'un video'}
                    </p>
                    <p className="text-xs text-stone-400">
                      {uploadType === 'photo' ? 'JPG, PNG, WebP (máx 10MB)' : 'MP4, WebM (máx 100MB)'}
                    </p>
                  </div>
                )}
              </label>
            </div>
            {selectedListener?.photo_url && uploadType === 'photo' && (
              <div>
                <p className="text-sm text-stone-500 mb-2">Foto actual:</p>
                <img 
                  src={getFullUrl(selectedListener.photo_url)} 
                  alt="Foto actual"
                  className="w-24 h-24 rounded-lg object-cover"
                />
              </div>
            )}
            {selectedListener?.video_url && uploadType === 'video' && (
              <div>
                <p className="text-sm text-stone-500 mb-2">Video actual:</p>
                <video 
                  src={getFullUrl(selectedListener.video_url)} 
                  className="w-full max-h-48 rounded-lg object-contain"
                  controls
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
